import { safeStorage } from "@/utils/safeStorage";
// Google Analytics utility functions

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

// Replace with your actual Google Analytics Measurement ID
const GA_MEASUREMENT_ID = 'G-8KG87G8WGT';

// Check if user has given analytics consent
const hasAnalyticsConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  const consent = safeStorage.getItem('cookie-consent');
  return consent === 'accepted';
};

// Initialize Google Analytics - now handled by CookieConsentContext
// This function is kept for backward compatibility
export const initGA = () => {
  // GA initialization is now handled by CookieConsentContext
  // to ensure proper consent management
  if (!hasAnalyticsConsent()) return;
  
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID);
  }
};

// Track page views - works in cookieless mode with Consent Mode v2
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    // GA Consent Mode v2 handles cookieless tracking automatically
    // We send the event and GA decides what data to collect based on consent
    console.log('[GA] ✅ Tracking page view:', pagePath, pageTitle);
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
      page_location: window.location.href,
    });
  } else if (typeof window !== 'undefined' && window.dataLayer) {
    // gtag might not be defined yet but dataLayer is - queue the event
    console.log('[GA] ⏳ Queueing page view (gtag loading):', pagePath);
    window.dataLayer.push({
      event: 'page_view',
      page_path: pagePath,
      page_title: pageTitle,
      page_location: window.location.href,
    });
  } else {
    console.warn('[GA] ❌ gtag and dataLayer not available - GA may be blocked');
  }
};

// Track custom events
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (!hasAnalyticsConsent()) return;
  
  if (typeof window !== 'undefined' && window.gtag) {
    console.log('[GA] ✅ Tracking event:', eventName, parameters);
    window.gtag('event', eventName, parameters);
  } else if (typeof window !== 'undefined' && window.dataLayer) {
    // Queue the event if gtag isn't ready yet
    console.log('[GA] ⏳ Queueing event:', eventName);
    window.dataLayer.push({
      event: eventName,
      ...parameters,
    });
  }
};

// Track horse tip events
export const trackHorseTip = (horseId: string, horseName: string, raceId: string) => {
  trackEvent('horse_tip_placed', {
    horse_id: horseId,
    horse_name: horseName,
    race_id: raceId,
    event_category: 'engagement',
    event_label: 'horse_tip'
  });
};

// Checkout funnel tracking
export const trackCheckoutStarted = (syndicateId: string, tier: string) => {
  trackEvent('checkout_started', {
    syndicate_id: syndicateId,
    membership_tier: tier,
    event_category: 'checkout_funnel',
  });
};

export const trackCheckoutGiftToggle = (isGift: boolean) => {
  trackEvent('checkout_gift_toggle', {
    is_gift: isGift,
    event_category: 'checkout_funnel',
  });
};

export const trackCheckoutTermsAgreed = () => {
  trackEvent('checkout_terms_agreed', {
    event_category: 'checkout_funnel',
  });
};

export const trackCheckoutStripeRedirect = (sessionId: string) => {
  trackEvent('checkout_stripe_redirect', {
    session_id: sessionId,
    event_category: 'checkout_funnel',
  });
};

export const trackCheckoutCompleted = (syndicateId: string, tier: string, amount: number) => {
  trackEvent('checkout_completed', {
    syndicate_id: syndicateId,
    membership_tier: tier,
    value: amount,
    currency: 'CZK',
    event_category: 'checkout_funnel',
  });
};

export const trackCheckoutFailed = (error: string, step: string) => {
  trackEvent('checkout_failed', {
    error_message: error,
    checkout_step: step,
    event_category: 'checkout_funnel',
  });
};

// Error tracking events
export const trackPaymentVerificationFailed = (sessionId: string, error: string) => {
  trackEvent('payment_verification_failed', {
    session_id: sessionId,
    error_message: error,
    event_category: 'errors',
  });
};

export const trackGiftCodeActivationFailed = (code: string, error: string) => {
  trackEvent('gift_code_activation_failed', {
    code,
    error_message: error,
    event_category: 'errors',
  });
};

export const trackRegistrationFailed = (error: string, method: string) => {
  trackEvent('registration_failed', {
    error_message: error,
    method,
    event_category: 'errors',
  });
};

export const trackLoginFailed = (error: string, method: string) => {
  trackEvent('login_failed', {
    error_message: error,
    method,
    event_category: 'errors',
  });
};

// User journey tracking
export const trackJourneyStart = (syndicateId: string, source?: string) => {
  trackEvent('journey_start', {
    syndicate_id: syndicateId,
    source: source || 'direct',
    event_category: 'user_journey',
  });
};

export const trackJourneyCheckoutView = (syndicateId: string) => {
  trackEvent('journey_checkout_view', {
    syndicate_id: syndicateId,
    event_category: 'user_journey',
  });
};

export const trackJourneyPaymentReturn = (sessionId: string, success: boolean) => {
  trackEvent('journey_payment_return', {
    session_id: sessionId,
    success,
    event_category: 'user_journey',
  });
};

export const trackJourneySuccess = (syndicateId: string, tier: string) => {
  trackEvent('journey_success', {
    syndicate_id: syndicateId,
    membership_tier: tier,
    event_category: 'user_journey',
  });
};

// Track navigation events
export const trackNavigation = (destination: string, source?: string) => {
  trackEvent('navigation', {
    destination,
    source: source || 'unknown',
    event_category: 'navigation'
  });
};

// Track search events
export const trackSearch = (searchTerm: string, searchType: string) => {
  trackEvent('search', {
    search_term: searchTerm,
    search_type: searchType,
    event_category: 'search'
  });
};

// Track odds analysis events
export const trackOddsAnalysis = (analysisType: string, eventId?: string, raceId?: string) => {
  trackEvent('odds_analysis_viewed', {
    analysis_type: analysisType,
    event_id: eventId,
    race_id: raceId,
    event_category: 'analysis'
  });
};

// Track horse/jockey detail views
export const trackDetailView = (itemType: 'horse' | 'jockey', itemId: string, itemName: string) => {
  trackEvent('detail_view', {
    item_type: itemType,
    item_id: itemId,
    item_name: itemName,
    event_category: 'content'
  });
};

// Track race day selection
export const trackRaceDaySelection = (eventId: string, location: string, date: string) => {
  trackEvent('race_day_selected', {
    event_id: eventId,
    location,
    date,
    event_category: 'engagement'
  });
};

// Track user authentication events
export const trackAuth = (action: 'login' | 'register' | 'logout', method?: string) => {
  trackEvent('auth_action', {
    action,
    method: method || 'email',
    event_category: 'authentication'
  });
};

// Track admin actions (for admin users)
export const trackAdminAction = (action: string, details?: Record<string, any>) => {
  trackEvent('admin_action', {
    action,
    ...details,
    event_category: 'admin'
  });
};